/**
 * Custom fine-art sizes (round 6). A customer-defined size is a `cu-{W}x{H}`
 * sizeId (inches, 0.1in precision, short edge first) priced by
 * `customPriceFor()` in @rpl/pricing — whole dollars, server-authoritative.
 * Fine-art family only; papers are the fine_art list minus any specialty
 * papers and never C-Type. This module holds the pure unit-conversion /
 * validation / catalog-entry helpers behind the "Custom size…" dialog.
 */

import {
  customPriceFor,
  customSizeId,
  customSizeInches,
  parseSizeId,
  type PaperId,
  type PricingData,
  type ResolvedSize,
} from '@rpl/pricing';

export type SizeUnit = 'in' | 'cm' | 'mm';

export const UNIT_LABELS: Record<SizeUnit, string> = { in: 'in', cm: 'cm', mm: 'mm' };

/** Convert a value in the given unit to inches (unrounded). */
export function toInches(value: number, unit: SizeUnit): number {
  if (unit === 'cm') return value / 2.54;
  if (unit === 'mm') return value / 25.4;
  return value;
}

/** Inches shown back in a unit (for hint copy), 1dp. */
export function fromInches(inches: number, unit: SizeUnit): number {
  const v = unit === 'cm' ? inches * 2.54 : unit === 'mm' ? inches * 25.4 : inches;
  return Math.round(v * 10) / 10;
}

/** Round to the sizeId's 0.1in precision (what actually gets ordered). */
export function snapInches(inches: number): number {
  return Math.round(inches * 10) / 10;
}

/** True when a sizeId is a customer-defined custom size. */
export function isCustomSizeId(sizeId: string): boolean {
  try {
    return !!parseSizeId(sizeId).custom;
  } catch {
    return false;
  }
}

export interface CustomSizeCheck {
  ok: boolean;
  /** Friendly, plain-language problem (when !ok). */
  error?: string;
}

/**
 * Validate a W×H (inches, already snapped) against customSizing min/max.
 * Orientation-agnostic: the short edge must fit the smaller max, the long edge
 * the larger max — mirrors customPriceFor's range check exactly.
 */
export function validateCustomInches(data: PricingData, wIn: number, hIn: number): CustomSizeCheck {
  const cs = data.customSizing;
  if (!cs?.enabled) return { ok: false, error: 'Custom sizing is not available right now.' };
  if (!(wIn > 0) || !(hIn > 0)) return { ok: false, error: 'Enter a width and a height.' };
  const lo = Math.min(wIn, hIn);
  const hi = Math.max(wIn, hIn);
  const [maxA, maxB] = cs.maxInches;
  const maxShort = Math.min(maxA, maxB);
  const maxLong = Math.max(maxA, maxB);
  if (lo < cs.minInches) {
    return { ok: false, error: `The smallest custom size is ${cs.minInches} inches on the short edge.` };
  }
  if (lo > maxShort || hi > maxLong) {
    return { ok: false, error: `The largest custom size is ${maxShort} x ${maxLong} inches.` };
  }
  return { ok: true };
}

/** Papers a custom size can be printed on: the fine-art list (no specialty, no C-Type). */
export function customPapers(data: PricingData): PaperId[] {
  return data.productLines.fine_art.papers.filter((p) => data.papers[p]?.priceTier !== 'specialty');
}

/** customPriceFor that returns null instead of throwing (invalid paper/range). */
export function customPriceSafe(data: PricingData, sizeId: string, paper: PaperId): number | null {
  try {
    return customPriceFor(data, sizeId, paper);
  } catch {
    return null;
  }
}

/** Display label for a custom size, matching the engine: `Custom 11.5 × 17"`. */
export function customSizeLabel(sizeId: string): string {
  const [w, h] = customSizeInches(sizeId);
  return `Custom ${w} × ${h}"`;
}

/**
 * Synthesized catalog entry for a custom sizeId, so custom sizes flow through
 * every code path that resolves sizes via the catalog map (crop editor,
 * buildPrintFields, previews, borders). Prices still come from priceFor /
 * customPriceFor — this entry carries geometry + labels only.
 */
export function customResolvedSize(sizeId: string): ResolvedSize {
  const inches = customSizeInches(sizeId);
  return {
    sizeId,
    family: 'fine_art',
    productLine: 'fine_art',
    size: parseSizeId(sizeId).size,
    label: customSizeLabel(sizeId),
    inches,
    pano: false,
  };
}

/** Build the cu- sizeId for a W×H in a unit (converted + snapped to 0.1in). */
export function customSizeIdFor(w: number, h: number, unit: SizeUnit): string {
  return customSizeId(snapInches(toInches(w, unit)), snapInches(toInches(h, unit)));
}

/**
 * A photo's NATIVE print size at `dpi` (default 300): width_px/dpi ×
 * height_px/dpi inches, snapped to the 0.1in order precision. Null when the
 * dimensions are missing/invalid (dialog falls back to empty fields).
 */
export function nativePrintInches(
  widthPx: number,
  heightPx: number,
  dpi = 300,
): { wIn: number; hIn: number } | null {
  if (!(widthPx > 0) || !(heightPx > 0) || !(dpi > 0)) return null;
  return { wIn: snapInches(widthPx / dpi), hIn: snapInches(heightPx / dpi) };
}

/**
 * Scale a W×H UNIFORMLY (aspect preserved) into the customSizing min/max
 * range, snapped to 0.1in: grow until the short edge reaches the minimum,
 * then shrink until short ≤ maxShort and long ≤ maxLong. Null when that
 * aspect simply cannot fit the range (extreme panoramas) or inputs are bad.
 */
export function clampCustomInches(
  data: PricingData,
  wIn: number,
  hIn: number,
): { wIn: number; hIn: number } | null {
  const cs = data.customSizing;
  if (!cs?.enabled || !(wIn > 0) || !(hIn > 0)) return null;
  const [maxA, maxB] = cs.maxInches;
  const maxShort = Math.min(maxA, maxB);
  const maxLong = Math.max(maxA, maxB);
  let w = wIn;
  let h = hIn;
  const grow = cs.minInches / Math.min(w, h);
  if (grow > 1) {
    w *= grow;
    h *= grow;
  }
  const shrink = Math.min(1, maxShort / Math.min(w, h), maxLong / Math.max(w, h));
  w = snapInches(w * shrink);
  h = snapInches(h * shrink);
  return validateCustomInches(data, w, h).ok ? { wIn: w, hIn: h } : null;
}

/**
 * Dialog prefill for a photo in context: its native print size at 300 DPI,
 * clamped (aspect kept) into the custom range, expressed in `unit` (1dp).
 * Null when there is no photo / no usable dimensions / the aspect cannot fit
 * the range — the dialog then keeps its usual empty defaults.
 */
export function customPrefill(
  data: PricingData,
  photo: { width: number; height: number } | null | undefined,
  unit: SizeUnit,
): { w: number; h: number } | null {
  if (!photo) return null;
  const native = nativePrintInches(photo.width, photo.height);
  if (!native) return null;
  const fitted = clampCustomInches(data, native.wIn, native.hIn);
  if (!fitted) return null;
  return { w: fromInches(fitted.wIn, unit), h: fromInches(fitted.hIn, unit) };
}
